import { Injectable } from '@angular/core';
import { Map } from 'maplibre-gl';
import {
  DAISI_INACTIVE_LAYER,
  DAISI_LAYER,
  DAISI_POPUP_INCIDENT_LAYER,
} from '@pages/overview-page/layers/daisi-layer';
import { BackgroundStyle } from '@services/map/abstract-map.service';
import {
  CLOSURES_VECTOR_STYLE,
  FCD_VECTOR_STYLE,
  HECTOMETRE_STYLE,
  IM_WEGEN_VECTOR_STYLE,
  RAYON_VECTOR_STYLE,
} from '@pages/overview-page/layers/vector-layers';
import {
  HIGHLIGHT_LINES_LAYER_ID,
  HIGHLIGHT_SYMBOLS_LAYER_ID,
  HIGHLIGHT_VECTOR_LINES_LAYER_ID,
  HIGHLIGHT_VECTOR_SYMBOLS_LAYER_ID,
} from '@services/map/highlight.service';
import { INCIDENT_HEATMAP_LAYER_ID } from '@services/map/incident-heatmap-layer';
import {
  INCIDENT_AREA_LAYER,
  INCIDENT_AREA_LINE_LAYER,
} from '@pages/overview-page/layers/incident-area-layer';
import { BEARING_LAYER } from '@daisi/pages/overview-page/layers/bearing-layer';
import { BRIDGES_SOURCE_ID } from '@pages/overview-page/layers/bridges-layer';
import { TRAFFIC_JAM_DETECTION_UNVERIFIED_SOURCE_ID } from '@pages/overview-page/layers/traffic-jam-detection-unverified-layer';
import { DRIPS_SOURCE_ID } from '@pages/overview-page/layers/drips-layer';
import { MSI_SOURCE_ID } from '@pages/overview-page/layers/msi-layer';
import { TRAFFIC_JAM_DETECTION_VERIFIED_SOURCE_ID } from '@pages/overview-page/layers/traffic-jam-detection-verified-layer';
import { ROADWORKS_SOURCE } from '@pages/roadworks-page/layers/roadworks-layers';

@Injectable({
  providedIn: 'root',
})
export class MapLayerOrderingService {
  readonly SYMBOL_LAYERS_IN_BACKGROUND = 'SYMBOL_LAYERS_IN_BACKGROUND';

  readonly layerOrdering = [
    HECTOMETRE_STYLE.id,
    HIGHLIGHT_SYMBOLS_LAYER_ID,
    HIGHLIGHT_LINES_LAYER_ID,
    HIGHLIGHT_VECTOR_SYMBOLS_LAYER_ID,
    HIGHLIGHT_VECTOR_LINES_LAYER_ID,
    'modal-click-point-layer',
    DAISI_POPUP_INCIDENT_LAYER.id,
    DAISI_LAYER.id,
    BEARING_LAYER.id,
    DAISI_INACTIVE_LAYER.id,
    INCIDENT_AREA_LINE_LAYER.id,
    INCIDENT_AREA_LAYER.id,
    BRIDGES_SOURCE_ID,
    DRIPS_SOURCE_ID,
    MSI_SOURCE_ID,
    TRAFFIC_JAM_DETECTION_UNVERIFIED_SOURCE_ID,
    TRAFFIC_JAM_DETECTION_VERIFIED_SOURCE_ID,
    this.SYMBOL_LAYERS_IN_BACKGROUND,
    BackgroundStyle.NDW_DEFAULT.symbolsId,
    BackgroundStyle.NDW_OSM_DEFAULT.symbolsId,
    BackgroundStyle.OSM_FRONT_OFFICE_LIGHT_WITH_NWB_ROADS.symbolsId,
    BackgroundStyle.OSM_FRONT_OFFICE_DARK.symbolsId,
    BackgroundStyle.OSM_GREEN.symbolsId,
    BackgroundStyle.AERIAL.symbolsId,
    BackgroundStyle.OSM_RASTER.symbolsId,
    FCD_VECTOR_STYLE.id,
    ROADWORKS_SOURCE,
    CLOSURES_VECTOR_STYLE.id,
    RAYON_VECTOR_STYLE.symbolsId,
    RAYON_VECTOR_STYLE.id,
    IM_WEGEN_VECTOR_STYLE.id,
    INCIDENT_HEATMAP_LAYER_ID,
    BackgroundStyle.NDW_DEFAULT.id,
    BackgroundStyle.NDW_OSM_DEFAULT.id,
    BackgroundStyle.OSM_FRONT_OFFICE_LIGHT_WITH_NWB_ROADS.id,
    BackgroundStyle.OSM_FRONT_OFFICE_DARK.id,
    BackgroundStyle.OSM_GREEN.id,
    BackgroundStyle.AERIAL.id,
    BackgroundStyle.OSM_RASTER.id,
  ];

  // function to determine the layerId to use for placing a new layer on the map
  getLayerBeforeId(layerId: string, map: Map): string | undefined {
    // use layerOrdering and current layers to determine where to place the layer
    const layers = map.getStyle().layers;
    // lookup the index of the layer in the layerOrdering array
    const layerIndex = this.layerOrdering.findIndex((l) => layerId.startsWith(l));
    if (layerIndex === -1) {
      console.error(`layerId ${layerId} not found in layerOrdering array`);
      return undefined;
    }
    if (layerIndex === 0) {
      return undefined;
    }

    let placeBeforeLayerId: string | undefined;
    // if there are layers above the layerOrdering array, loop through all the layers in the map to find any matches
    for (let i = layerIndex - 1; i >= 0; i--) {
      const existingLayer = layers.find((l) => l.id.startsWith(this.layerOrdering[i]));
      if (existingLayer) {
        placeBeforeLayerId = existingLayer.id;
        break;
      }
    }

    return placeBeforeLayerId;
  }
}
